import { useTranslation } from 'react-i18next';
import { StepTrackerItem, type StepState } from './StepTrackerItem';

export type { StepState };

export type StepTrackerVariant = 'default' | 'compact';

interface StepTrackerProps {
  activeStep: number;
  steps: string[];
  stepStates?: ReadonlyArray<StepState>;
  /**
   * `compact` names only the step you are on, for hosts too narrow to carry every label
   * (the wizard's 408px configuration panel). The default names all of them.
   */
  variant?: StepTrackerVariant;
}

const resolveState = (index: number, activeStep: number, override?: StepState): StepState => {
  if (override !== undefined) return override;
  if (index === activeStep) return 'active';
  return index < activeStep ? 'complete' : 'inactive';
};

export const StepTracker = ({
  activeStep,
  steps,
  stepStates,
  variant = 'default',
}: StepTrackerProps) => {
  const { t } = useTranslation('common');

  return (
    // A div carrying the roles rather than an `ol`: laying a list out with flex drops its
    // implicit semantics in WebKit, so the steps stop being announced as a list of N, and
    // declaring the role back on an `ol` is what `jsx-a11y/no-redundant-roles` rejects.
    <div
      data-testid="step-tracker"
      data-variant={variant}
      role="list"
      aria-label={t('components.stepTracker.stepsLabel')}
      className="flex w-full shrink-0 items-center justify-center gap-2 border-b border-border-light bg-background px-6 py-4"
    >
      {steps.map((label, index) => {
        const state = resolveState(index, activeStep, stepStates?.[index]);

        return (
          <StepTrackerItem
            key={label}
            label={label}
            index={index}
            state={state}
            showLabel={variant === 'default' || state === 'active'}
            showConnector={index < steps.length - 1}
          />
        );
      })}
    </div>
  );
};
